'use client';

import {
  Children,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import {
  Pin,
  Play,
  Pause,
  Plus,
  Pencil,
  Copy,
  Check,
  Share2,
  MessageCircle,
  UserPlus,
  UserMinus,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Heart,
  MessageSquare,
  X,
  Image as ImageIcon,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/browser';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { useUserConnections } from '@/hooks/useUserConnections';
import { getOrCreateDirectConversation } from '@/lib/dm/getOrCreateDirectConversation';
import { resolveDisplayName } from '@/lib/displayName';
import { useToast } from '@/hooks/useToast';
import { Avatar, Button as DsButton, Card, Tag } from '@/components/ds';
import ProfileSectionEditor from '@/components/profile/ProfileSectionEditor';
import { cn } from '@/lib/utils';
import type { UserProfile } from '@/lib/userUtils';

// ── Types ────────────────────────────────────────────────────────────
interface Reel {
  id: string;
  label?: string;
  file_url: string;
  file_name: string;
  created_at: string;
  user_id: string;
  file_type: string;
  visibility?: string;
}
interface PortfolioPhoto {
  id: string;
  file_url: string;
  public_url?: string;
  file_name: string;
  created_at: string;
  display_order?: number;
}
interface MixPost {
  id: string;
  content: string;
  createdAt: string;
  likes: number;
  comments: number;
  photoUrl: string | null;
}

type BannerTheme = 'ember' | 'velvet' | 'twilight' | 'moss';
const BANNERS: { key: BannerTheme; deep: string; glow: string }[] = [
  { key: 'ember', deep: '#4A3A10', glow: '#B08A2E' },
  { key: 'velvet', deep: '#471824', glow: '#A03A55' },
  { key: 'twilight', deep: '#232051', glow: '#6058C9' },
  { key: 'moss', deep: '#16352A', glow: '#3E8A63' },
];
function isBanner(v: unknown): v is BannerTheme {
  return v === 'ember' || v === 'velvet' || v === 'twilight' || v === 'moss';
}

// ── Talent-detail display helpers ────────────────────────────────────
const VOICE_TYPE_LABELS: Record<string, string> = {
  masculine: 'Masculine',
  feminine: 'Feminine',
  androgynous: 'Androgynous',
  neutral: 'Neutral',
};
const UNION_LABELS: Record<string, string> = {
  union: 'Union',
  'non-union': 'Non-union',
};
const RATE_UNIT_SUFFIX: Record<string, string> = {
  per_hour: '/hr',
  per_project: '/project',
  per_word: '/word',
  per_finished_minute: '/finished min',
};
function formatCastableAge(min: number | null, max: number | null): string | null {
  if (min != null && max != null) return `Plays ${min}–${max}`;
  if (min != null) return `Plays ${min}+`;
  if (max != null) return `Plays up to ${max}`;
  return null;
}
function formatMinRate(cents: number, unit: string | null): string {
  const dollars = cents / 100;
  const amount = Number.isInteger(dollars) ? `$${dollars}` : `$${dollars.toFixed(2)}`;
  return `${amount}${unit ? RATE_UNIT_SUFFIX[unit] ?? '' : ''}`;
}

interface ProfileMixClientProps {
  params: { username: string };
  initialProfile: UserProfile | null;
  initialPublicData: Partial<UserProfile> | null;
  initialReels?: Reel[];
  initialPhotos?: PortfolioPhoto[];
  initialIsFollowing?: boolean;
  initialFollowersCount?: number;
  initialFollowingCount?: number;
  initialIsOwnProfile?: boolean;
}

const PROFILE_MEDIA_BUCKET = 'user-assets';
// Same limit the old ProfileClient/PortfolioPhotoGrid enforced.
const MAX_PHOTOS = 5;

// Pass-through full URLs; resolve raw storage paths against the public bucket.
function resolveStorageUrl(
  supabase: ReturnType<typeof createClient>,
  value: string | null | undefined
): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return supabase.storage
    .from(PROFILE_MEDIA_BUCKET)
    .getPublicUrl(trimmed.replace(/^\/+/, '')).data.publicUrl;
}

// Deterministic waveform heights from a seed — ported from the prototype rdWave.
function rdWave(seed: number, n: number): number[] {
  const out: number[] = [];
  let x = seed * 9301 + 49297;
  for (let i = 0; i < n; i++) {
    x = (x * 233280 + 9301) % 233280;
    const r = x / 233280;
    const envelope = 0.35 + 0.65 * Math.sin((i / n) * Math.PI);
    out.push(0.18 + r * 0.82 * envelope);
  }
  return out;
}
function seedFromId(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return (h % 1000) / 1000 + 0.12;
}
function stripExt(name: string): string {
  return name.replace(/\.[^.]+$/, '');
}
function reelTitle(r: Reel): string {
  return (r.label && r.label.trim()) || stripExt(r.file_name) || 'Demo reel';
}
function reelKind(r: Reel): 'audio' | 'video' {
  return (r.file_type || '').toLowerCase().startsWith('video') ? 'video' : 'audio';
}
function fmtTime(sec: number): string {
  if (!isFinite(sec) || sec <= 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!t) return '';
  const diff = Math.max(0, Date.now() - t);
  const d = Math.floor(diff / 86400000);
  if (d >= 7) return `${Math.floor(d / 7)}w`;
  if (d >= 1) return `${d}d`;
  const h = Math.floor(diff / 3600000);
  if (h >= 1) return `${h}h`;
  const m = Math.floor(diff / 60000);
  return m >= 1 ? `${m}m` : 'now';
}
function firstImage(media: unknown): string | null {
  if (!media) return null;
  const arr = Array.isArray(media) ? media : [media];
  for (const m of arr) {
    if (typeof m === 'string') return m;
    if (m && typeof m === 'object') {
      const o = m as Record<string, unknown>;
      const url = (o.url || o.file_url || o.src || o.publicUrl) as string | undefined;
      const type = String(o.type || o.file_type || '');
      if (url && (!type || type.startsWith('image'))) return url;
    }
  }
  return null;
}

// ── Waveform ─────────────────────────────────────────────────────────
function Waveform({
  seed,
  bars,
  height,
  progress,
  gap = 2,
}: {
  seed: number;
  bars: number;
  height: number;
  progress: number;
  gap?: number;
}) {
  const heights = useMemo(() => rdWave(seed, bars), [seed, bars]);
  return (
    <div
      aria-hidden="true"
      className="flex items-center w-full min-w-0 overflow-hidden"
      style={{ gap, height }}
    >
      {heights.map((h, i) => (
        <span
          key={i}
          className="flex-1 min-w-0 rounded-[2px]"
          style={{
            height: `${Math.round(h * 100)}%`,
            background: i / bars < progress ? 'var(--wave-progress)' : 'var(--wave-track)',
          }}
        />
      ))}
    </div>
  );
}

// ── Small bits ───────────────────────────────────────────────────────
function MonoLabel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn('font-mono text-[10px] uppercase tracking-[0.09em]', className)}
    >
      {children}
    </span>
  );
}

function EditDot({ onClick, label = 'Edit' }: { onClick: () => void; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="grid h-[26px] w-[26px] min-h-0 min-w-0 place-items-center rounded-full border border-border-default bg-surface-2 text-text-secondary hover:text-text-primary hover:bg-surface-3 transition-colors"
    >
      <Pencil className="h-3 w-3" strokeWidth={1.75} />
    </button>
  );
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <MonoLabel className="text-text-tertiary">{label}</MonoLabel>
      <span className="text-[13.5px] text-text-secondary">{value}</span>
    </div>
  );
}

function DetailChips({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
      <MonoLabel className="text-text-tertiary">{label}</MonoLabel>
      <div className="flex flex-wrap gap-1.5">
        {items.map((s) => (
          <Tag key={s} className="border border-border-default bg-transparent">
            {s}
          </Tag>
        ))}
      </div>
    </div>
  );
}

function Stat({
  n,
  label,
  sub,
}: {
  n: number | string;
  label: string;
  sub?: string;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[17px] font-semibold tabular-nums leading-tight" style={{ color: 'var(--hdr-text)' }}>
        {n}
      </span>
      <span className="text-[11px]" style={{ color: 'var(--hdr-text-2)' }}>
        {label}
        {sub ? <span> · {sub}</span> : null}
      </span>
    </div>
  );
}

function OpenSection({
  label,
  right,
  children,
}: {
  label: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="border-t border-border-default pt-3.5">
      <div className="mb-3 flex items-center gap-2.5">
        <MonoLabel className="text-text-tertiary">{label}</MonoLabel>
        <div className="flex-1" />
        {right}
      </div>
      {children}
    </section>
  );
}

function HorizontalRail({
  title,
  count,
  children,
}: {
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  // Each rail owns its own scroll ref, so its arrows page ONLY its own content.
  const ref = useRef<HTMLDivElement>(null);
  const [canScroll, setCanScroll] = useState(false);
  // childCount is a dep so the overflow check re-runs when items are added
  // (e.g. a photo upload) — adding a child grows scrollWidth without changing
  // the container's own box size, which a ResizeObserver alone wouldn't catch.
  const childCount = Children.count(children);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => setCanScroll(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    window.addEventListener('resize', check);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', check);
    };
  }, [childCount]);

  const page = (dir: number) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.9, 360), behavior: 'smooth' });
  };

  return (
    <section>
      <div className="mb-3 flex items-center gap-2.5">
        <h3 className="text-[15px] font-semibold tracking-[-0.005em] text-text-primary">{title}</h3>
        <MonoLabel className="text-accent">{count}</MonoLabel>
        <div className="flex-1" />
        {/* Arrows only when the content actually overflows the rail. */}
        {canScroll && (
          <>
            <button
              type="button"
              onClick={() => page(-1)}
              aria-label="Scroll left"
              className="hidden h-7 w-7 min-h-0 min-w-0 place-items-center rounded-full border border-border-subtle bg-surface-2 text-text-secondary hover:text-text-primary hover:bg-surface-3 sm:grid"
            >
              <ChevronLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={() => page(1)}
              aria-label="Scroll right"
              className="hidden h-7 w-7 min-h-0 min-w-0 place-items-center rounded-full border border-border-subtle bg-surface-2 text-text-secondary hover:text-text-primary hover:bg-surface-3 sm:grid"
            >
              <ChevronRight className="h-3.5 w-3.5" strokeWidth={1.75} />
            </button>
          </>
        )}
      </div>
      <div
        ref={ref}
        className="flex gap-3 overflow-x-auto pb-1 snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {children}
      </div>
    </section>
  );
}

// ── Main ─────────────────────────────────────────────────────────────
export default function ProfileMixClient({
  params,
  initialProfile,
  initialReels = [],
  initialPhotos = [],
  initialIsFollowing = false,
  initialFollowersCount = 0,
  initialFollowingCount = 0,
  initialIsOwnProfile = false,
}: ProfileMixClientProps) {
  const router = useRouter();
  const { user } = useAuthContext();
  const { toast } = useToast();
  const { followUser, unfollowUser } = useUserConnections();
  const supabase = useMemo(() => createClient(), []);

  const [profile, setProfile] = useState<UserProfile | null>(initialProfile);
  const p = (profile || {}) as Record<string, any>;
  const isOwn = initialIsOwnProfile;

  const reels = initialReels;
  // Photos are stateful so a fresh upload appears without a full refetch.
  const [photos, setPhotos] = useState<PortfolioPhoto[]>(initialPhotos.slice(0, MAX_PHOTOS));
  const [photoUploading, setPhotoUploading] = useState(false);
  const [bannerTheme, setBannerTheme] = useState<BannerTheme>(
    isBanner(p.banner_theme) ? p.banner_theme : 'twilight'
  );
  const [featuredId, setFeaturedId] = useState<string | null>(
    (p.featured_reel_id as string) || null
  );
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [followLoading, setFollowLoading] = useState(false);
  const [followersCount] = useState(initialFollowersCount);
  const [followingCount] = useState(initialFollowingCount);
  const [copied, setCopied] = useState(false);
  const [editorSection, setEditorSection] = useState<string | null>(null);
  const [changeFeaturedOpen, setChangeFeaturedOpen] = useState(false);
  const [completionDismissed, setCompletionDismissed] = useState(false);
  const [posts, setPosts] = useState<MixPost[]>([]);
  const [activePill, setActivePill] = useState<'all' | 'posts' | 'photos' | 'about'>('all');

  // Shared single-audio player state.
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [durations, setDurations] = useState<Record<string, number>>({});

  // Section anchors.
  const topRef = useRef<HTMLDivElement>(null);
  const postsRef = useRef<HTMLDivElement>(null);
  const photosRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);

  const displayName = resolveDisplayName(p as any, 'Member');
  const username = (p.username as string) || params.username;
  const role = (p.role as string) || '';
  const location =
    (p.location as string) ||
    [p.city, p.state].filter(Boolean).join(', ') ||
    '';
  const tagline = (p.voice_tagline as string) || '';
  const bio = (p.bio as string) || '';
  // Fall back on emptiness, not array-ness: profiles.skills is `text[] DEFAULT
  // '{}'`, so it is *always* an array and an Array.isArray() test never reaches
  // specialties — which left users whose only data is in specialties looking at
  // an empty Skills section.
  const skills: string[] = p.skills?.length ? p.skills : (p.specialties ?? []);
  const memberSince = p.created_at
    ? new Date(p.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : null;
  const avatarUrl = resolveStorageUrl(supabase, p.avatar_url);

  // Talent-discovery fields (Phase 2). Blank/empty values are hidden below.
  const voiceAgeMin = typeof p.voice_age_min === 'number' ? p.voice_age_min : null;
  const voiceAgeMax = typeof p.voice_age_max === 'number' ? p.voice_age_max : null;
  const voiceTypes: string[] = Array.isArray(p.voice_type) ? p.voice_type : [];
  const unionStatus: string[] = Array.isArray(p.union_status) ? p.union_status : [];
  const openToUnpaid = !!p.open_to_unpaid;
  const minRateCents = typeof p.min_rate_cents === 'number' ? p.min_rate_cents : null;
  const rateUnit = (p.rate_unit as string) || null;
  const castableAgeLabel = formatCastableAge(voiceAgeMin, voiceAgeMax);
  const rateLabel = minRateCents != null ? formatMinRate(minRateCents, rateUnit) : null;
  const hasTalentDetails =
    !!castableAgeLabel ||
    voiceTypes.length > 0 ||
    unionStatus.length > 0 ||
    openToUnpaid ||
    rateLabel != null;

  const audioReels = reels.filter((r) => reelKind(r) === 'audio').length;
  const videoReels = reels.filter((r) => reelKind(r) === 'video').length;
  const featured = reels.find((r) => r.id === featuredId) || reels[0] || null;
  const otherReels = reels.filter((r) => !featured || r.id !== featured.id);

  // ── Posts (separate-query rule, no FK joins) ──
  useEffect(() => {
    if (!p.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: raw } = await supabase
          .from('community_posts')
          .select('id, content, media, created_at')
          .eq('user_id', p.id)
          .eq('is_deleted', false)
          .order('created_at', { ascending: false })
          .limit(12);
        const rows = Array.isArray(raw) ? raw : [];
        if (rows.length === 0) {
          if (!cancelled) setPosts([]);
          return;
        }
        const ids = rows.map((r: any) => r.id);
        const safe = async (q: PromiseLike<{ data: any[] | null; error: any }>) => {
          try {
            const { data } = await q;
            return data || [];
          } catch {
            return [];
          }
        };
        const [likeRows, commentRows] = await Promise.all([
          safe(supabase.from('post_likes').select('post_id').in('post_id', ids) as any),
          safe(supabase.from('post_comments').select('post_id').in('post_id', ids) as any),
        ]);
        const likeCount: Record<string, number> = {};
        likeRows.forEach((l: any) => (likeCount[l.post_id] = (likeCount[l.post_id] || 0) + 1));
        const commentCount: Record<string, number> = {};
        commentRows.forEach((c: any) => (commentCount[c.post_id] = (commentCount[c.post_id] || 0) + 1));
        if (cancelled) return;
        setPosts(
          rows.map((r: any) => ({
            id: r.id,
            content: r.content || '',
            createdAt: r.created_at,
            likes: likeCount[r.id] || 0,
            comments: commentCount[r.id] || 0,
            photoUrl: resolveStorageUrl(supabase, firstImage(r.media)),
          }))
        );
      } catch {
        if (!cancelled) setPosts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [p.id, supabase]);

  // ── Anchor pills active-state via IntersectionObserver ──
  useEffect(() => {
    const map: { id: 'posts' | 'photos' | 'about'; el: HTMLElement | null }[] = [
      { id: 'posts', el: postsRef.current },
      { id: 'photos', el: photosRef.current },
      { id: 'about', el: aboutRef.current },
    ];
    const observed = map.filter((m) => m.el) as { id: 'posts' | 'photos' | 'about'; el: HTMLElement }[];
    if (observed.length === 0) return;
    const visible = new Map<Element, number>();
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => visible.set(e.target, e.isIntersecting ? e.intersectionRatio : 0));
        const ranked = observed
          .map((m) => ({ id: m.id, ratio: visible.get(m.el) || 0 }))
          .filter((r) => r.ratio > 0)
          .sort((a, b) => b.ratio - a.ratio);
        setActivePill(ranked.length > 0 ? ranked[0].id : 'all');
      },
      { rootMargin: '-120px 0px -55% 0px', threshold: [0, 0.25, 0.5, 1] }
    );
    observed.forEach((m) => io.observe(m.el));
    return () => io.disconnect();
  }, [posts.length, photos.length]);

  const scrollTo = (pill: 'all' | 'posts' | 'photos' | 'about') => {
    setActivePill(pill);
    const target =
      pill === 'all'
        ? topRef.current
        : pill === 'posts'
          ? postsRef.current
          : pill === 'photos'
            ? photosRef.current
            : aboutRef.current;
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Player ──
  const togglePlay = useCallback(
    (reel: Reel) => {
      const el = audioRef.current;
      if (!el) return;
      const url = resolveStorageUrl(supabase, reel.file_url);
      if (!url) return;
      if (playingId === reel.id) {
        if (isPlaying) {
          el.pause();
        } else {
          void el.play().catch(() => undefined);
        }
        return;
      }
      el.src = url;
      el.currentTime = 0;
      setPlayingId(reel.id);
      setProgress(0);
      void el.play().catch(() => undefined);
    },
    [playingId, isPlaying, supabase]
  );

  // ── Visitor actions ──
  const handleMessage = async () => {
    if (!user?.id || !p.id) {
      router.push(`/login?redirect=/u/${username}`);
      return;
    }
    try {
      const convId = await getOrCreateDirectConversation(supabase, user.id, p.id);
      router.push(`/messages/${convId}`);
    } catch {
      toast({ title: 'Could not open messages', description: 'Please try again.', variant: 'destructive' });
    }
  };

  const handleFollow = async () => {
    if (!user?.id) {
      router.push(`/login?redirect=/u/${username}`);
      return;
    }
    if (!p.id || followLoading) return;
    setFollowLoading(true);
    const was = isFollowing;
    setIsFollowing(!was);
    try {
      if (was) await unfollowUser(p.id);
      else await followUser(p.id);
    } catch {
      setIsFollowing(was);
      toast({ title: "Couldn't update follow", description: 'Try again in a moment.', variant: 'destructive' });
    } finally {
      setFollowLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      const url =
        typeof window !== 'undefined'
          ? `${window.location.origin}/u/${username}`
          : `/u/${username}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast({ title: 'Copy failed', description: 'Could not copy the link.', variant: 'destructive' });
    }
  };

  // ── Own-view writes ──
  const pickBanner = async (b: BannerTheme) => {
    if (!isOwn || !p.id) return;
    const prev = bannerTheme;
    setBannerTheme(b);
    const { error } = await supabase.from('profiles').update({ banner_theme: b }).eq('id', p.id);
    if (error) {
      setBannerTheme(prev);
      toast({ title: 'Could not save banner', description: error.message, variant: 'destructive' });
    }
  };

  const setFeatured = async (reelId: string) => {
    if (!isOwn || !p.id) return;
    const prev = featuredId;
    setFeaturedId(reelId);
    setChangeFeaturedOpen(false);
    const { error } = await supabase
      .from('profiles')
      .update({ featured_reel_id: reelId })
      .eq('id', p.id);
    if (error) {
      setFeaturedId(prev);
      toast({ title: 'Could not pin demo', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'Featured demo updated' });
    }
  };

  // ── Portfolio photo upload (ported verbatim from ProfileClient/
  //    PortfolioPhotoGrid: user-assets bucket, portfolio/<uid>/<ts>-<rand>.<ext>
  //    path, media_files insert with the full publicUrl, max 5). ──
  const handlePhotoUpload = async (file: File) => {
    if (!isOwn || !p.id) return;
    if (photos.length >= MAX_PHOTOS) {
      toast({ title: 'Photo limit reached', description: `You can add up to ${MAX_PHOTOS} photos.` });
      return;
    }
    setPhotoUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `portfolio/${p.id}/${Date.now()}-${Math.random()
        .toString(36)
        .substring(7)}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from(PROFILE_MEDIA_BUCKET)
        .upload(path, file, { upsert: false, contentType: file.type });
      if (upErr) {
        toast({ title: 'Upload error', description: upErr.message, variant: 'destructive' });
        return;
      }
      const { data: { publicUrl } } = supabase.storage
        .from(PROFILE_MEDIA_BUCKET)
        .getPublicUrl(path);
      const { data: newPhoto, error: dbErr } = await supabase
        .from('media_files')
        .insert({
          user_id: p.id,
          file_type: 'image',
          file_url: publicUrl,
          file_name: file.name,
          file_size: file.size,
          mime_type: file.type,
          label: 'Portfolio Photo',
          visibility: 'public',
          display_order: photos.length,
        })
        .select()
        .single();
      if (dbErr || !newPhoto) {
        toast({
          title: 'Upload warning',
          description: `Photo uploaded but failed to save: ${dbErr?.message || 'unknown error'}.`,
          variant: 'destructive',
        });
        return;
      }
      setPhotos((prev) => [...prev, newPhoto as PortfolioPhoto]);
      toast({ title: 'Photo uploaded' });
    } catch {
      toast({ title: 'Upload error', description: 'Failed to upload photo. Try again.', variant: 'destructive' });
    } finally {
      setPhotoUploading(false);
    }
  };

  const handlePhotoDelete = async (id: string) => {
    if (!isOwn || !p.id) return;
    const prev = photos;
    setPhotos((cur) => cur.filter((ph) => ph.id !== id));
    const { error } = await supabase
      .from('media_files')
      .delete()
      .eq('id', id)
      .eq('user_id', p.id);
    if (error) {
      setPhotos(prev);
      toast({ title: 'Could not remove photo', description: error.message, variant: 'destructive' });
    }
  };

  // ── Completion (own only) ──
  const completion = useMemo(() => {
    const items: { ok: boolean; next: string }[] = [
      { ok: !!p.avatar_url, next: 'add a profile photo' },
      { ok: !!bio, next: 'write a short bio' },
      { ok: !!location, next: 'add your location' },
      { ok: !!tagline, next: 'add a voice tagline' },
      { ok: skills.length > 0, next: 'list your skills' },
      { ok: reels.length > 0, next: 'upload a demo reel' },
      { ok: photos.length > 0, next: 'add a portfolio photo' },
    ];
    const done = items.filter((i) => i.ok).length;
    const next = items.find((i) => !i.ok)?.next || null;
    return { pct: Math.round((done / items.length) * 100), next };
  }, [p.avatar_url, bio, location, tagline, skills.length, reels.length, photos.length]);

  // currentData scoped per editor section so .update() never clobbers unrelated columns.
  const editorData = (section: string): Record<string, any> => {
    switch (section) {
      case 'about':
        return {
          full_name: p.full_name || '',
          display_name: p.display_name || '',
          preferred_name: p.preferred_name || '',
          username: p.username || '',
          bio: p.bio || '',
          voice_tagline: p.voice_tagline || '',
          location: p.location || '',
        };
      case 'skills':
        return {
          skills: Array.isArray(p.skills) ? p.skills : [],
          specialties: Array.isArray(p.specialties) ? p.specialties : [],
        };
      case 'talent-details':
        return {
          voice_age_min: p.voice_age_min ?? '',
          voice_age_max: p.voice_age_max ?? '',
          voice_type: Array.isArray(p.voice_type) ? p.voice_type : [],
          union_status: Array.isArray(p.union_status) ? p.union_status : [],
          open_to_unpaid: !!p.open_to_unpaid,
          min_rate_cents: typeof p.min_rate_cents === 'number' ? p.min_rate_cents : null,
          rate_unit: p.rate_unit ?? '',
        };
      case 'avatar':
        return { avatar_url: p.avatar_url || '' };
      default:
        return {};
    }
  };

  const rootStyle: CSSProperties = { background: 'var(--surface-0)' };

  return (
    <div data-banner={bannerTheme} className="text-text-primary" style={rootStyle}>
      <audio
        ref={audioRef}
        className="hidden"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false);
          setProgress(0);
        }}
        onTimeUpdate={(e) => {
          const el = e.currentTarget;
          if (el.duration > 0) setProgress(el.currentTime / el.duration);
        }}
        onLoadedMetadata={(e) => {
          const el = e.currentTarget;
          if (playingId && el.duration > 0) {
            setDurations((d) => ({ ...d, [playingId]: el.duration }));
          }
        }}
      />

      {/* ── Header band (banner wash) ── */}
      <div
        ref={topRef}
        className="border-b border-border-subtle px-5 pt-7 pb-5 sm:px-8"
        style={{ background: 'var(--hdr-bg)' }}
      >
        <div className="mx-auto flex max-w-[1120px] flex-wrap items-center gap-5">
          <div className="relative">
            <Avatar name={displayName} src={avatarUrl || undefined} size={92} square ring status="online" />
            {isOwn && (
              <div className="absolute -right-2 -top-2">
                <EditDot onClick={() => setEditorSection('avatar')} label="Change photo" />
              </div>
            )}
          </div>

          <div className="min-w-[240px] flex-1">
            <div className="flex flex-wrap items-center gap-2.5">
              <h1
                className="text-[26px] font-bold leading-tight tracking-[-0.015em]"
                style={{ color: 'var(--hdr-text)' }}
              >
                {displayName}
              </h1>
              {role && <Tag className="border border-border-default bg-transparent">{role}</Tag>}
            </div>
            <div className="mt-1 text-[13px]" style={{ color: 'var(--hdr-text-2)' }}>
              @{username}
              {location ? ` · ${location}` : ''}
            </div>
            {tagline && (
              <p
                className="mt-2 max-w-[560px] text-[14px] leading-[1.45]"
                style={{ color: 'var(--hdr-text)' }}
              >
                {tagline}
              </p>
            )}
          </div>

          <div className="flex flex-col items-end gap-2.5">
            <div className="flex flex-wrap items-center justify-end gap-2">
              {isOwn ? (
                <>
                  <DsButton variant="secondary" onClick={handleCopyLink}>
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    {copied ? 'Copied' : 'Copy Link'}
                  </DsButton>
                  <DsButton variant="primary" onClick={() => setEditorSection('about')}>
                    <Pencil className="h-3.5 w-3.5" />
                    Edit profile
                  </DsButton>
                </>
              ) : (
                <>
                  <DsButton variant="secondary" onClick={handleMessage}>
                    <MessageCircle className="h-3.5 w-3.5" />
                    Message
                  </DsButton>
                  <DsButton
                    variant={isFollowing ? 'secondary' : 'primary'}
                    onClick={handleFollow}
                    disabled={followLoading}
                  >
                    {isFollowing ? <UserMinus className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
                    {isFollowing ? 'Following' : 'Follow'}
                  </DsButton>
                  <DsButton variant="secondary" onClick={handleCopyLink} aria-label="Share profile" className="px-2.5">
                    {copied ? <Check className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
                  </DsButton>
                </>
              )}
            </div>
            {isOwn && (
              <BannerPicker current={bannerTheme} onPick={pickBanner} />
            )}
          </div>
        </div>

        <div className="mx-auto mt-4 flex max-w-[1120px] flex-wrap gap-9">
          <Stat n={followersCount} label="Followers" />
          <Stat n={followingCount} label="Following" />
          <Stat
            n={reels.length}
            label="Demo reels"
            sub={`${audioReels} audio · ${videoReels} video`}
          />
          <Stat n={posts.length} label="Posts" />
        </div>
      </div>

      {/* ── Body ── */}
      <div className="mx-auto flex max-w-[1120px] flex-col gap-6 px-5 pb-12 pt-5 sm:px-8">
        {isOwn && !completionDismissed && (
          <div className="flex items-center gap-3.5 border-t border-border-default py-3">
            <MonoLabel className="text-accent whitespace-nowrap">Profile {completion.pct}% complete</MonoLabel>
            <div className="h-[5px] flex-1 overflow-hidden rounded-full" style={{ background: 'var(--wave-track)' }}>
              <div
                className="h-full rounded-full bg-accent transition-all"
                style={{ width: `${completion.pct}%` }}
              />
            </div>
            {completion.next && (
              <span className="hidden whitespace-nowrap text-[12px] text-text-secondary sm:inline">
                Next: {completion.next}
              </span>
            )}
            <button
              type="button"
              onClick={() => setCompletionDismissed(true)}
              aria-label="Dismiss"
              className="grid h-6 w-6 min-h-0 min-w-0 place-items-center text-text-tertiary hover:text-text-primary"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Featured + more demos */}
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_340px]">
          <FeaturedHero
            reel={featured}
            isOwn={isOwn}
            playing={!!featured && playingId === featured.id && isPlaying}
            progress={featured && playingId === featured.id ? progress : 0.3}
            duration={featured ? durations[featured.id] : undefined}
            onPlay={() => featured && togglePlay(featured)}
            onChangeFeatured={() => setChangeFeaturedOpen((v) => !v)}
            onUpload={() => setEditorSection('demo-reels')}
            changeOpen={changeFeaturedOpen}
            reels={reels}
            featuredId={featured?.id || null}
            onPickFeatured={setFeatured}
          />
          <div className="flex flex-col gap-2.5">
            <MonoLabel className="text-text-tertiary">More demos</MonoLabel>
            {otherReels.map((r) => (
              <MiniReel
                key={r.id}
                reel={r}
                playing={playingId === r.id && isPlaying}
                progress={playingId === r.id ? progress : 0}
                onPlay={() => togglePlay(r)}
              />
            ))}
            {otherReels.length === 0 && (
              <p className="px-1 text-[12px] text-text-tertiary">No other demos yet.</p>
            )}
            {isOwn && (
              <button
                type="button"
                onClick={() => setEditorSection('demo-reels')}
                className="flex h-11 items-center justify-center gap-2 rounded-[14px] border border-dashed border-border-strong text-[13px] text-text-secondary hover:text-text-primary hover:bg-surface-2 transition-colors"
              >
                <Plus className="h-3.5 w-3.5" strokeWidth={1.75} />
                Upload a demo
              </button>
            )}
          </div>
        </div>

        {/* Anchor pills */}
        <div className="flex flex-wrap items-center gap-2">
          {([
            ['all', 'All'],
            ['posts', 'Posts'],
            ['photos', 'Photos'],
            ['about', 'About'],
          ] as const).map(([key, label]) => {
            const active = activePill === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => scrollTo(key)}
                aria-pressed={active}
                className={cn(
                  'h-8 min-h-0 rounded-full px-4 text-[13px] font-medium transition-colors',
                  active ? 'bg-pill-active-bg text-pill-active-text' : 'bg-pill-bg text-pill-text hover:text-text-primary'
                )}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Posts rail */}
        <div ref={postsRef} className="scroll-mt-24">
          {posts.length > 0 ? (
            <HorizontalRail title="Posts" count={posts.length}>
              {posts.map((post) => (
                <PostCard key={post.id} post={post} authorName={displayName} authorAvatar={avatarUrl} />
              ))}
            </HorizontalRail>
          ) : (
            <section>
              <div className="mb-3 flex items-center gap-2.5">
                <h3 className="text-[15px] font-semibold text-text-primary">Posts</h3>
                <MonoLabel className="text-accent">0</MonoLabel>
              </div>
              <Card className="flex items-center justify-center rounded-[14px] border-dashed py-7 text-[13px] text-text-tertiary">
                {isOwn ? 'Your community posts will appear here.' : 'No posts yet.'}
              </Card>
            </section>
          )}
        </div>

        {/* Photos rail */}
        <div ref={photosRef} className="scroll-mt-24">
          {photos.length > 0 || isOwn ? (
            <HorizontalRail title="Photos" count={photos.length}>
              {photos.map((ph) => (
                <PhotoTile
                  key={ph.id}
                  url={resolveStorageUrl(supabase, ph.public_url || ph.file_url)}
                  name={ph.file_name}
                  onDelete={isOwn ? () => handlePhotoDelete(ph.id) : undefined}
                />
              ))}
              {isOwn && photos.length < MAX_PHOTOS && (
                <AddPhotoTile uploading={photoUploading} onPick={handlePhotoUpload} />
              )}
            </HorizontalRail>
          ) : (
            <section>
              <div className="mb-3 flex items-center gap-2.5">
                <h3 className="text-[15px] font-semibold text-text-primary">Photos</h3>
                <MonoLabel className="text-accent">0</MonoLabel>
              </div>
              <Card className="flex items-center justify-center rounded-[14px] border-dashed py-7 text-[13px] text-text-tertiary">
                {isOwn ? 'Add portfolio photos from the editor.' : 'No photos yet.'}
              </Card>
            </section>
          )}
        </div>

        {/* Talent details + About + Skills */}
        <div ref={aboutRef} className="flex scroll-mt-24 flex-col gap-8">
          {(hasTalentDetails || isOwn) && (
            <OpenSection
              label="Talent details"
              right={isOwn ? <EditDot onClick={() => setEditorSection('talent-details')} /> : undefined}
            >
              {hasTalentDetails ? (
                <div className="flex flex-col gap-3">
                  {castableAgeLabel && <DetailRow label="Castable age" value={castableAgeLabel} />}
                  {voiceTypes.length > 0 && (
                    <DetailChips label="Voice type" items={voiceTypes.map((v) => VOICE_TYPE_LABELS[v] ?? v)} />
                  )}
                  {unionStatus.length > 0 && (
                    <DetailChips label="Union" items={unionStatus.map((v) => UNION_LABELS[v] ?? v)} />
                  )}
                  {(rateLabel || openToUnpaid) && (
                    <DetailRow
                      label="Rate"
                      value={[rateLabel, openToUnpaid ? 'Open to unpaid' : null]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                  )}
                </div>
              ) : (
                <p className="text-[13.5px] text-text-tertiary">
                  Add castable age, voice type, union status, and rates so scouts can find you.
                </p>
              )}
            </OpenSection>
          )}

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <OpenSection label="About" right={isOwn ? <EditDot onClick={() => setEditorSection('about')} /> : undefined}>
              {bio ? (
                <p className="text-[13.5px] leading-[1.6] text-text-secondary whitespace-pre-wrap">{bio}</p>
              ) : (
                <p className="text-[13.5px] text-text-tertiary">{isOwn ? 'Add a short bio about your voice work.' : 'No bio yet.'}</p>
              )}
              {memberSince && (
                <div className="mt-3 flex items-center gap-1.5 text-[12px] text-text-tertiary">
                  <Calendar className="h-3 w-3" strokeWidth={1.75} />
                  Member since {memberSince}
                </div>
              )}
            </OpenSection>

            <OpenSection label="Skills & specialties" right={isOwn ? <EditDot onClick={() => setEditorSection('skills')} /> : undefined}>
              {skills.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {skills.map((s) => (
                    <Tag key={s} className="border border-border-default bg-transparent">
                      {s}
                    </Tag>
                  ))}
                </div>
              ) : (
                <p className="text-[13.5px] text-text-tertiary">{isOwn ? 'List the skills you want scouts to see.' : 'No skills listed yet.'}</p>
              )}
            </OpenSection>
          </div>
        </div>
      </div>

      {editorSection && p.id && (
        <ProfileSectionEditor
          sectionId={editorSection}
          userId={p.id}
          currentData={editorData(editorSection)}
          onSave={(data: any) => {
            setProfile((prev) => (prev ? { ...prev, ...data } : prev));
            if (isBanner((data as any)?.banner_theme)) setBannerTheme((data as any).banner_theme);
          }}
          onClose={() => setEditorSection(null)}
          onRefresh={() => router.refresh()}
        />
      )}
    </div>
  );
}

// ── Featured hero ────────────────────────────────────────────────────
function FeaturedHero({
  reel,
  isOwn,
  playing,
  progress,
  duration,
  onPlay,
  onChangeFeatured,
  onUpload,
  changeOpen,
  reels,
  featuredId,
  onPickFeatured,
}: {
  reel: Reel | null;
  isOwn: boolean;
  playing: boolean;
  progress: number;
  duration?: number;
  onPlay: () => void;
  onChangeFeatured: () => void;
  onUpload: () => void;
  changeOpen: boolean;
  reels: Reel[];
  featuredId: string | null;
  onPickFeatured: (id: string) => void;
}) {
  if (!reel) {
    return (
      <Card className="flex flex-col gap-3.5 rounded-[14px] p-5">
        <div className="flex items-center gap-2">
          <Pin className="h-3 w-3 text-accent" strokeWidth={2} />
          <MonoLabel className="text-accent">Featured demo</MonoLabel>
        </div>
        {isOwn ? (
          <button
            type="button"
            onClick={onUpload}
            className="flex h-[88px] items-center justify-center gap-2 rounded-[14px] border border-dashed border-border-strong text-[13px] text-text-secondary hover:text-text-primary hover:bg-surface-3 transition-colors"
          >
            <Plus className="h-4 w-4" strokeWidth={1.75} />
            Upload your first demo
          </button>
        ) : (
          <p className="py-6 text-center text-[13px] text-text-tertiary">No demos yet.</p>
        )}
      </Card>
    );
  }

  return (
    <Card className="relative flex flex-col gap-3.5 rounded-[14px] p-5">
      <div className="flex items-center gap-2">
        <Pin className="h-3 w-3 text-accent" strokeWidth={2} />
        <MonoLabel className="text-accent">Featured demo</MonoLabel>
        <div className="flex-1" />
        {isOwn && reels.length > 0 && (
          <DsButton variant="secondary" size="sm" onClick={onChangeFeatured}>
            <Pencil className="h-3 w-3" />
            Change featured
          </DsButton>
        )}
      </div>

      {changeOpen && (
        <div className="rounded-[10px] border border-border-subtle bg-surface-3 p-2">
          <MonoLabel className="px-1 text-text-tertiary">Pin a demo</MonoLabel>
          <div className="mt-1 flex flex-col">
            {reels.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => onPickFeatured(r.id)}
                className={cn(
                  'flex items-center justify-between rounded-sm px-2 py-1.5 text-left text-[13px] hover:bg-surface-2',
                  r.id === featuredId ? 'text-accent' : 'text-text-primary'
                )}
              >
                <span className="truncate">{reelTitle(r)}</span>
                {r.id === featuredId && <Check className="h-3.5 w-3.5 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onPlay}
          aria-label={playing ? 'Pause' : 'Play'}
          className="grid h-14 w-14 flex-shrink-0 place-items-center rounded-full bg-accent text-text-on-accent hover:bg-accent-hover"
        >
          {playing ? <Pause className="h-5 w-5" /> : <Play className="ml-0.5 h-5 w-5" />}
        </button>
        <div className="min-w-0 flex-1">
          <div className="mb-2.5 flex min-w-0 items-baseline gap-2.5">
            <strong className="truncate text-[17px] tracking-[-0.01em]">{reelTitle(reel)}</strong>
            <span className="flex-shrink-0 text-[12px] tabular-nums text-text-tertiary">
              {duration ? fmtTime(duration) : reelKind(reel) === 'video' ? 'Video' : 'Audio'}
            </span>
          </div>
          <Waveform seed={seedFromId(reel.id)} bars={88} height={52} progress={progress} />
        </div>
      </div>
    </Card>
  );
}

// ── Mini reel ────────────────────────────────────────────────────────
function MiniReel({
  reel,
  playing,
  progress,
  onPlay,
}: {
  reel: Reel;
  playing: boolean;
  progress: number;
  onPlay: () => void;
}) {
  return (
    <Card className="flex items-center gap-3 rounded-[14px] p-3">
      <button
        type="button"
        onClick={onPlay}
        aria-label={playing ? 'Pause' : 'Play'}
        className="grid h-[34px] w-[34px] flex-shrink-0 place-items-center rounded-full bg-surface-3 text-text-primary hover:bg-surface-2"
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="ml-0.5 h-3.5 w-3.5" />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-medium">{reelTitle(reel)}</div>
        <div className="mt-0.5 text-[11px] text-text-tertiary">
          {reelKind(reel) === 'video' ? 'Video' : 'Audio'}
        </div>
      </div>
      <div className="w-[70px] flex-shrink-0">
        <Waveform seed={seedFromId(reel.id)} bars={22} height={22} progress={progress} gap={1.5} />
      </div>
    </Card>
  );
}

// ── Post card ────────────────────────────────────────────────────────
// The whole card navigates to the post, matching the home feed's MixedPostCard.
// The avatar is intentionally NOT a profile link here: it would nest an <a> inside
// this <a>, and it would only point back to the profile already being viewed.
function PostCard({
  post,
  authorName,
  authorAvatar,
}: {
  post: MixPost;
  authorName: string;
  authorAvatar: string | null;
}) {
  return (
    <Link
      href={`/community/posts/${post.id}`}
      className="block w-[320px] flex-shrink-0 snap-start rounded-[14px] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
    >
      <Card hoverable className="flex h-full flex-col gap-2.5 rounded-[14px] p-3.5">
        <div className="flex items-center gap-2">
          <Avatar name={authorName} src={authorAvatar || undefined} size={26} square />
          <span className="text-[12px] font-medium">{authorName}</span>
          <span className="text-[11px] text-text-tertiary">· {relTime(post.createdAt)}</span>
        </div>
        <p
          className="m-0 overflow-hidden text-[13px] leading-[1.5] text-text-primary"
          style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}
        >
          {post.content}
        </p>
        {post.photoUrl && (
          <div className="relative aspect-[16/8] overflow-hidden rounded-lg bg-surface-3">
            <Image src={post.photoUrl} alt="" fill sizes="320px" className="object-cover" unoptimized />
          </div>
        )}
        <div className="mt-auto flex gap-3.5 text-[11px] text-text-tertiary">
          <span className="inline-flex items-center gap-1">
            <Heart className="h-3 w-3" strokeWidth={1.75} />
            {post.likes}
          </span>
          <span className="inline-flex items-center gap-1">
            <MessageSquare className="h-3 w-3" strokeWidth={1.75} />
            {post.comments}
          </span>
        </div>
      </Card>
    </Link>
  );
}

// ── Photo tile ───────────────────────────────────────────────────────
function PhotoTile({
  url,
  name,
  onDelete,
}: {
  url: string | null;
  name: string;
  onDelete?: () => void;
}) {
  return (
    <div className="group relative w-[200px] flex-shrink-0 snap-start">
      <div className="relative aspect-[4/5] overflow-hidden rounded-[14px] border border-border-subtle bg-surface-3">
        {url ? (
          <Image src={url} alt={name} fill sizes="200px" className="object-cover" unoptimized />
        ) : (
          <div className="grid h-full place-items-center text-text-tertiary">
            <ImageIcon className="h-6 w-6" strokeWidth={1.5} />
          </div>
        )}
      </div>
      {onDelete && (
        <button
          type="button"
          onClick={onDelete}
          aria-label="Remove photo"
          className="absolute right-2 top-2 grid h-7 w-7 min-h-0 min-w-0 place-items-center rounded-full border border-border-strong bg-surface-2 text-text-secondary opacity-0 transition-opacity hover:text-text-primary group-hover:opacity-100"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

// ── Add-photo tile (own view) — dashed slot mirroring "Upload a demo" ──
function AddPhotoTile({
  uploading,
  onPick,
}: {
  uploading: boolean;
  onPick: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="w-[200px] flex-shrink-0 snap-start">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="flex aspect-[4/5] w-full flex-col items-center justify-center gap-2 rounded-[14px] border border-dashed border-border-strong text-[13px] text-text-secondary transition-colors hover:bg-surface-2 hover:text-text-primary disabled:opacity-60"
      >
        {uploading ? (
          <>
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-accent" />
            <span>Uploading…</span>
          </>
        ) : (
          <>
            <Plus className="h-6 w-6" strokeWidth={1.75} />
            <span>Add photo</span>
          </>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) {
            onPick(f);
            e.target.value = '';
          }
        }}
      />
    </div>
  );
}

// ── Banner picker (own) ──────────────────────────────────────────────
function BannerPicker({
  current,
  onPick,
}: {
  current: BannerTheme;
  onPick: (b: BannerTheme) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 rounded-full border border-border-subtle bg-surface-2 px-2.5 py-1.5">
      <span className="mr-0.5 text-[11px] text-text-tertiary">Banner</span>
      {BANNERS.map((b) => (
        <button
          key={b.key}
          type="button"
          title={b.key}
          aria-label={`${b.key} banner`}
          onClick={() => onPick(b.key)}
          className="h-4 w-4 min-h-0 min-w-0 rounded-[5px]"
          style={{
            background: `linear-gradient(135deg, ${b.glow}, ${b.deep})`,
            outline: b.key === current ? '2px solid var(--text-primary)' : 'none',
            outlineOffset: 1.5,
          }}
        />
      ))}
    </div>
  );
}
